import {
  CompanyTerminationFeeSettingsFixed as GqlCompanyTerminationFeeSettingsFixed,
  CompanyTerminationFeeSettingsPercent as GqlCompanyTerminationFeeSettingsPercent,
  CompanyTerminationFeeType as GqlCompanyTerminationFeeType,
  LoanTerminationFee as GqlLoanTerminationFee,
  LoanTerminationFeeFixed,
  LoanTerminationFeePercent,
  LoanTerminationFeeType as GqlLoanTerminationFeeType,
} from '@willow/graphql-iso/src/app';
import { assertNever, CompanyTerminationFeeSettings, LoanTerminationFee } from '@willow/types-iso';

import { getNumMonthsBetweenDates } from '../utils/dates';
import { calculateFixedFee, calculatePercentFee, feeDisabledByLoanOverride } from '../utils/feeCalculations';

export function getTerminationFeeAmount(
  companyFeeSettings: CompanyTerminationFeeSettings,
  firstDueDate: Date,
  principal: number,
  numProperties: number,
  loanOverrides: LoanTerminationFee | undefined,
): number;
export function getTerminationFeeAmount(
  companyFeeSettings: GqlCompanyTerminationFeeSettingsFixed | GqlCompanyTerminationFeeSettingsPercent,
  firstDueDate: Date,
  principal: number,
  numProperties: number,
  loanOverrides: GqlLoanTerminationFee | undefined,
): number;
export function getTerminationFeeAmount(
  companyFeeSettings:
    | CompanyTerminationFeeSettings
    | GqlCompanyTerminationFeeSettingsFixed
    | GqlCompanyTerminationFeeSettingsPercent,
  firstDueDate: Date,
  principal: number,
  numProperties: number,
  loanOverrides: LoanTerminationFee | GqlLoanTerminationFee | undefined,
): number {
  const numMonthsSinceFirstDueDate = getNumMonthsBetweenDates(firstDueDate, new Date());

  // If the override flag is present and false, ignore the company-level setting even if it is enabled
  if (feeDisabledByLoanOverride(loanOverrides)) {
    return 0;
  }

  if (loanOverrides) {
    switch (loanOverrides.type) {
      case 'percent':
      case GqlLoanTerminationFeeType.Percent: {
        if ('percentMonths' in loanOverrides && numMonthsSinceFirstDueDate <= loanOverrides.percentMonths) {
          return 0;
        }
        const percentOverride = loanOverrides as LoanTerminationFeePercent;
        return calculatePercentFee({
          percentPrincipal: percentOverride.percentPrincipal,
          principal,
        });
      }
      case 'fixed':
      case GqlLoanTerminationFeeType.Fixed: {
        if ('fixedMonths' in loanOverrides && numMonthsSinceFirstDueDate <= loanOverrides.fixedMonths) {
          return 0;
        }
        const fixedOverride = loanOverrides as LoanTerminationFeeFixed;
        return calculateFixedFee({
          fixedAmount: fixedOverride.fixedAmount,
          perProperty: fixedOverride.perProperty,
          numProperties,
        });
      }
      // For type completeness only, but the flag check above will handle this case.
      case 'disabled':
      case GqlLoanTerminationFeeType.Disabled: {
        return 0;
      }
      default:
        assertNever(loanOverrides);
    }
  }

  const { enabled } = companyFeeSettings;
  if (!enabled) return 0;

  switch (companyFeeSettings.type) {
    case 'percent':
    case GqlCompanyTerminationFeeType.Percent: {
      if ('percentMonths' in companyFeeSettings && numMonthsSinceFirstDueDate <= companyFeeSettings.percentMonths) {
        return 0;
      }

      return calculatePercentFee({
        percentPrincipal: 'percentPrincipal' in companyFeeSettings ? companyFeeSettings.percentPrincipal : 0,
        principal,
      });
    }
    case 'fixed':
    case GqlCompanyTerminationFeeType.Fixed: {
      if ('fixedMonths' in companyFeeSettings && numMonthsSinceFirstDueDate <= companyFeeSettings.fixedMonths) {
        return 0;
      }
      return calculateFixedFee({
        fixedAmount: 'fixedAmount' in companyFeeSettings ? companyFeeSettings.fixedAmount : 0,
        perProperty: 'perProperty' in companyFeeSettings ? companyFeeSettings.perProperty : false,
        numProperties,
      });
    }
    default:
      assertNever(companyFeeSettings);
  }
}
